import React, { useMemo } from 'react';

/**
 * Regole del contenuto delle comunicazioni. Devono restare identiche a
 * MessageContent in apps/tools/src/components/DashboardAnnouncements.tsx:
 * il portale rende solo testo, formattazione di base e link HTTP(S), senza
 * attributi, script o risorse incorporate.
 */
const BLOCKED = ['script', 'style', 'iframe', 'object', 'svg', 'form', 'template'];
const ALLOWED = ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'blockquote', 'code'];

/** Rende il contenuto come lo vedrà l'utente nella bacheca del portale. */
export const MessageContent: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
    const nodes = useMemo(() => {
        const doc = new DOMParser().parseFromString(content, 'text/html');
        const render = (node: Node, key: number): React.ReactNode => {
            if (node.nodeType === Node.TEXT_NODE) return node.textContent;
            if (!(node instanceof Element)) return null;
            const tag = node.tagName.toLowerCase();
            if (BLOCKED.includes(tag)) return null;
            const children = Array.from(node.childNodes).map(render);
            if (tag === 'a') {
                const href = node.getAttribute('href') ?? '';
                if (/^https?:\/\//i.test(href)) {
                    return <a key={key} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                }
            }
            if (ALLOWED.includes(tag)) {
                return React.createElement(tag, { key }, tag === 'br' ? undefined : children);
            }
            return <React.Fragment key={key}>{children}</React.Fragment>;
        };
        return Array.from(doc.body.childNodes).map(render);
    }, [content]);
    return <div className={className}>{nodes}</div>;
};

/**
 * Riduce l'HTML prodotto dall'editor al sottoinsieme ammesso, cosi' cio' che
 * si salva e' esattamente cio' che il portale mostrera'. I <div> creati dal
 * contentEditable diventano paragrafi.
 */
export const sanitizeMessageHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const out = document.implementation.createHTMLDocument('');

    const copy = (node: Node, parent: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            parent.appendChild(out.createTextNode(node.textContent ?? ''));
            return;
        }
        if (!(node instanceof Element)) return;
        let tag = node.tagName.toLowerCase();
        if (BLOCKED.includes(tag)) return;
        if (tag === 'div') tag = 'p';
        if (tag === 'b') tag = 'strong';
        if (tag === 'i') tag = 'em';

        let target: Node = parent;
        if (tag === 'a') {
            const href = node.getAttribute('href') ?? '';
            if (/^https?:\/\//i.test(href)) {
                const a = out.createElement('a');
                a.setAttribute('href', href);
                target = parent.appendChild(a);
            }
        } else if (ALLOWED.includes(tag)) {
            target = parent.appendChild(out.createElement(tag));
        }
        node.childNodes.forEach(child => copy(child, target));
    };

    doc.body.childNodes.forEach(child => copy(child, out.body));

    // Testo libero al primo livello → paragrafo, per un markup regolare.
    const body = out.body;
    Array.from(body.childNodes).forEach(child => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
            const p = out.createElement('p');
            body.replaceChild(p, child);
            p.appendChild(child);
        }
    });
    // Paragrafi vuoti in coda (Invio di troppo) via.
    while (body.lastChild && (body.lastChild.textContent ?? '').trim() === '') {
        body.removeChild(body.lastChild);
    }
    return body.innerHTML;
};

/** Testo semplice (per anteprime e conteggio caratteri). */
export const messagePlainText = (html: string): string =>
    new DOMParser().parseFromString(html, 'text/html').body.textContent?.replace(/\s+/g, ' ').trim() ?? '';

export const MESSAGE_TYPES = [
    { value: 'Info', label: 'Comunicazione', icon: 'bi-megaphone', hint: 'Avviso generale' },
    { value: 'Success', label: 'Novità', icon: 'bi-stars', hint: 'Nuovi tool o funzioni' },
    { value: 'Warning', label: 'Attenzione', icon: 'bi-exclamation-triangle', hint: 'Manutenzioni, cambi in arrivo' },
    { value: 'Error', label: 'Avviso importante', icon: 'bi-exclamation-octagon', hint: 'Disservizi, azioni richieste' },
] as const;

export type MessageType = typeof MESSAGE_TYPES[number]['value'];

export const messageTypeMeta = (type: string) =>
    MESSAGE_TYPES.find(t => t.value === type) ?? MESSAGE_TYPES[0];

export const MESSAGE_STATUS: Record<string, { label: string; badge: string; icon: string }> = {
    Published: { label: 'In bacheca', badge: 'bg-success', icon: 'bi-broadcast' },
    Scheduled: { label: 'Programmata', badge: 'bg-info', icon: 'bi-calendar-event' },
    Draft: { label: 'Bozza', badge: 'bg-secondary', icon: 'bi-pencil' },
    Expired: { label: 'Scaduta', badge: 'bg-light', icon: 'bi-clock-history' },
};
